import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { nonQuinstreetStates, policyType as PolicyType } from '@ourbranch/lookups';

import { capitalize } from 'core/helpers/formatters';
import { useToast } from 'core/components/toast';
import { useScroll } from 'core/hooks/use-scroll';
import { AuthContext } from 'core/components/auth';
import { useStore } from 'core/store/store.mobx';
import FormList from 'core/components/form-list';
import { QuinStreet } from 'common/components/quinstreet';
import AddPerson from 'common/components/people/add-form';
import Suggestions from 'common/components/people/suggestions/suggestions';
import NonRatedIndividuals from 'common/components/people/non-rated-individuals';
import { OfferDriver, OfferApplicant } from './person';
import useStyles from './people-details.styles';

const PeopleDetails = observer(({ disabled, hasAuto }) => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const {
    values: { drivers, removedDrivers },
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const toast = useToast();

  const session = useContext(AuthContext);
  const isQuinstreetAvailable = session.canQuoteQuinstreet && !nonQuinstreetStates.includes(store.state);
  useScroll(store?.scrollTo);
  const isApiOffer = store.isApiOffer;

  const checkForNonRatedIndividualsAndRemove = useCallback(
    (person) => {
      const firstName = person.firstName.trim().toUpperCase();
      const lastName = person.lastName.trim().toUpperCase();

      const personAlreadyAsNonRatedIndividual = removedDrivers.some(
        (driver) =>
          driver.firstName === firstName && driver.lastName === lastName && driver.dateOfBirth === person.dateOfBirth
      );
      if (personAlreadyAsNonRatedIndividual) {
        setFieldValue(
          'removedDrivers',
          removedDrivers.filter(
            (driver) =>
              driver.firstName !== firstName &&
              driver.lastName !== lastName &&
              driver.dateOfBirth !== person.dateOfBirth
          )
        );
        setFieldTouched('removedDrivers');
        toast.notify({
          type: 'success',
          message: `${capitalize(person.firstName)} ${capitalize(person.lastName)} was successfully added as a driver and removed as a non-rated individual.`
        });
      }
    },
    [removedDrivers, setFieldValue, setFieldTouched, toast]
  );

  return (
    <div className={classes.container}>
      <FormList
        disabled={disabled}
        id="drivers" // this is confusing but no matter the policy type (e.g. home only) the people data will always be under the "drivers" node for offers
        item={hasAuto ? OfferDriver : OfferApplicant}
        addForm={(props) => (
          <AddPerson
            {...props}
            drivers={drivers}
            hasAuto={hasAuto}
            addContent={<Suggestions isCoapplicant={!hasAuto} isDriver drivers={drivers} />}
          />
        )}
        addLabel={hasAuto ? 'Add Drivers' : 'Add Co-applicants'}
        title={hasAuto ? 'Drivers' : 'Applicants'}
        singular="person"
        plural="people"
        hasAuto={hasAuto}
        onAdd={checkForNonRatedIndividualsAndRemove}
      />
      <NonRatedIndividuals openByDefault={isApiOffer} />
      {isQuinstreetAvailable && <QuinStreet policyType={PolicyType.Auto} />}
      {isQuinstreetAvailable && <QuinStreet policyType={PolicyType.Home} />}
    </div>
  );
});

export default PeopleDetails;

PeopleDetails.propTypes = {
  disabled: PropTypes.bool,
  hasAuto: PropTypes.bool
};

PeopleDetails.defaultProps = {
  disabled: false,
  hasAuto: false
};
